import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import SearchableSelect from '../ui/SearchableSelect';
import EmailTemplateService from '../../services/EmailTemplateService';
import EmailService from '../../services/EmailService';
import { DEFAULT_TEMPLATE_SAMPLES, TEMPLATE_PLACEHOLDERS } from '../../data/emailTemplates';
import { renderTemplate } from '../../utils/templateRenderer';
import { sanitizeHtml } from '../../utils/emailSanitizer';

const buildOptions = (templates) =>
  templates.map((template) => ({ value: template.key, label: template.name, meta: template.key }));

export default function EmailTemplateManager({ currentUser }) {
  const templateService = useMemo(() => new EmailTemplateService(), []);
  const emailService = useMemo(() => new EmailService(), []);
  const [templates, setTemplates] = useState(() => templateService.list());
  const [selectedKey, setSelectedKey] = useState(templates[0]?.key || '');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [editorMode, setEditorMode] = useState('visual');
  const [testEmail, setTestEmail] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setTemplates(templateService.list());
  }, [templateService]);

  useEffect(() => {
    const template = templates.find((item) => item.key === selectedKey) || templates[0];
    if (!template) return;
    setSelectedKey(template.key);
    setSubject(template.subject || '');
    setHtmlBody(template.html_body || '');
  }, [templates, selectedKey]);

  useEffect(() => {
    if (editorMode !== 'visual') return;
    if (!editorRef.current) return;
    editorRef.current.innerHTML = htmlBody || '';
  }, [editorMode, htmlBody]);

  const sampleData = DEFAULT_TEMPLATE_SAMPLES[selectedKey] || {};
  const placeholders = TEMPLATE_PLACEHOLDERS[selectedKey] || [];

  const handleSave = () => {
    const template = templates.find((item) => item.key === selectedKey);
    if (!template) return;
    const sanitized = sanitizeHtml(htmlBody);
    const updated = templateService.update(
      {
        ...template,
        subject: subject.trim() || template.subject,
        html_body: sanitized
      },
      currentUser
    );
    setTemplates((prev) => prev.map((item) => (item.key === updated.key ? updated : item)));
    setStatusMessage('Template saved.');
    window.setTimeout(() => setStatusMessage(''), 2000);
  };

  const handleSendTest = () => {
    if (!testEmail.trim()) {
      setStatusMessage('Enter a test email address.');
      return;
    }
    const log = emailService.sendTemplate({
      templateKey: selectedKey,
      to: testEmail.trim(),
      data: sampleData,
      entity: { type: 'template', id: selectedKey }
    });
    if (log.status === 'failed') {
      setStatusMessage(`Test failed: ${log.error_message}`);
    } else {
      setStatusMessage('Test email queued.');
    }
    window.setTimeout(() => setStatusMessage(''), 2000);
  };

  const applyCommand = (command, value = null) => {
    if (editorMode !== 'visual') return;
    if (!document.queryCommandSupported?.(command)) return;
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setHtmlBody(editorRef.current.innerHTML);
    }
  };

  const insertLink = () => {
    const url = window.prompt('Enter link URL');
    if (!url) return;
    applyCommand('createLink', url);
  };

  const handleImageInsert = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result?.toString();
      if (!result) return;
      applyCommand('insertImage', result);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const previewHtml = sanitizeHtml(renderTemplate(htmlBody, sampleData));

  return (
    <Card className="p-6 space-y-4">
      <div>
        <h3 className="sp-value" style={{ fontWeight: 600 }}>Email templates</h3>
        <p className="sp-muted">Edit email content used across the platform.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="sp-label">Template</label>
          <SearchableSelect
            value={selectedKey}
            options={buildOptions(templates)}
            onChange={(value) => setSelectedKey(value)}
            placeholder="Select template"
            className="mt-2"
          />
          <div className="text-xs sp-muted mt-2">Key: {selectedKey}</div>
        </div>
        <div className="md:col-span-2">
          <label className="sp-label">Subject</label>
          <input
            className="sp-input mt-2 w-full"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button size="sm" variant={editorMode === 'visual' ? 'primary' : 'ghost'} onClick={() => setEditorMode('visual')}>
            Visual
          </Button>
          <Button size="sm" variant={editorMode === 'html' ? 'primary' : 'ghost'} onClick={() => setEditorMode('html')}>
            HTML
          </Button>
          {editorMode === 'visual' && (
            <>
              <Button size="sm" variant="ghost" onClick={() => applyCommand('bold')} type="button">
                Bold
              </Button>
              <Button size="sm" variant="ghost" onClick={() => applyCommand('italic')} type="button">
                Italic
              </Button>
              <Button size="sm" variant="ghost" onClick={() => applyCommand('insertUnorderedList')} type="button">
                Bullet list
              </Button>
              <Button size="sm" variant="ghost" onClick={insertLink} type="button">
                Insert link
              </Button>
              <Button size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()} type="button">
                Insert image
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageInsert}
              />
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {statusMessage && <span className="text-xs sp-muted">{statusMessage}</span>}
          <Button size="sm" variant="secondary" onClick={handleSave} type="button">
            Save template
          </Button>
        </div>
      </div>

      {editorMode === 'visual' ? (
        <div
          ref={editorRef}
          className="sp-textarea w-full min-h-[180px]"
          contentEditable
          aria-label="Email template editor"
          suppressContentEditableWarning
          onInput={(event) => setHtmlBody(event.currentTarget.innerHTML)}
        />
      ) : (
        <textarea
          className="sp-textarea w-full min-h-[180px]"
          value={htmlBody}
          onChange={(event) => setHtmlBody(event.target.value)}
          aria-label="Email template HTML editor"
        />
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <div className="sp-label">Placeholders</div>
          <div className="sp-panel sp-panel--muted p-3 mt-2 text-xs">
            {placeholders.length ? (
              placeholders.map((item) => (
                <div key={item} className="mb-1">{`{{${item}}}`}</div>
              ))
            ) : (
              <div>No placeholders defined.</div>
            )}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="sp-label">Preview</div>
          <div
            className="sp-panel sp-panel--muted p-3 mt-2 text-sm"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          <div className="flex items-center gap-2 mt-3">
            <input
              className="sp-input w-full"
              placeholder="Send test email to"
              value={testEmail}
              onChange={(event) => setTestEmail(event.target.value)}
              type="email"
            />
            <Button size="sm" variant="secondary" type="button" onClick={handleSendTest}>
              Send test
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
